/* eslint-disable max-lines */
import React, {
    useEffect, useMemo, useState
} from 'react';
import {
    useNavigate, useParams
} from 'react-router-dom';
import {
    opcodeAssistantService,
} from 'shared/api';
import {
    Bot, Layers, Radio, Rocket, Loader2
} from 'lucide-react';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from 'shared/components/ui/alert-dialog';
import {
    cn
} from 'shared/lib/utils';
import DSResourceList from '../library/DSResourceList';
import BlueprintModal from './BlueprintModal';
import ProvisionDialog from '../lib/ProvisionDialog';
import WorkforceFilterSidebar from './WorkforceFilterSidebar';
import {
    EMPTY_WORKFORCE_FILTERS,
    buildWorkforceFacetCounts,
    filterAssistants,
    hasActiveWorkforceFilters,
} from './workforceFilters';
import './WorkforceFilterSidebar.css';

/**
 * UnifiedBuilder — DE Studio → Workforce hub.
 *
 * Provision lives in the row action menu via extraAction.
 */
export default function UnifiedBuilder() {
    const navigate = useNavigate();
    const {
        oragID, subOragID
    } = useParams();
    const [assistants, setAssistants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showBp, setShowBp] = useState(false);
    const [mode, setMode] = useState('gui');
    const [refreshTick, setRefreshTick] = useState(0);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [provisionFor, setProvisionFor] = useState(null);
    const [filters, setFilters] = useState({ ...EMPTY_WORKFORCE_FILTERS });

    const facetCounts = useMemo(
        () => buildWorkforceFacetCounts(assistants),
        [assistants],
    );

    const filteredAssistants = useMemo(
        () => filterAssistants(assistants, filters),
        [assistants, filters],
    );

    const filtersActive = hasActiveWorkforceFilters(filters);

    useEffect(() => {
        if (!oragID || !subOragID) return;
        setLoading(true);
        setError(null);
        opcodeAssistantService.listOpcodeAssistants(oragID, subOragID)
            .then((res) => {
                const list = Array.isArray(res?.data) ? res.data : [];
                list.sort((a, b) => new Date(b.created || 0) - new Date(a.created || 0));
                setAssistants(list);
            })
            .catch((err) => setError(err?.message || 'Failed to load'))
            .finally(() => setLoading(false));
    }, [oragID, subOragID, refreshTick]);

    function openWorkspace(a) {
        navigate(`/organizations/${oragID}/sub/${subOragID}/de-studio/unified-builder/${a.assistant_id}`);
    }

    function handleDeployed(newAssistantId) {
        setShowBp(false);
        setRefreshTick((t) => t + 1);
        setTimeout(() => navigate(`/organizations/${oragID}/sub/${subOragID}/de-studio/unified-builder/${newAssistantId}`), 250);
    }

    async function handleDelete(a) {
        setIsDeleting(true);
        try {
            await opcodeAssistantService.deleteOpcodeAssistant({
                organizationId: oragID,
                subOrganizationId: subOragID,
                assistantId: a.assistant_id,
            });
            setConfirmDelete(null);
            setRefreshTick((t) => t + 1);
        } catch (err) {
            alert('Delete failed: ' + (err?.response?.data?.message || err?.message || String(err)));
        } finally {
            setIsDeleting(false);
        }
    }

    return (
        <div className="ds-hub-page" style={{ padding: '20px 28px', minHeight: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
            <div className="wf-filter-shell">
                <div className="wf-filter-body">
                    <WorkforceFilterSidebar
                        filters={filters}
                        onChange={setFilters}
                        facetCounts={facetCounts}
                        filteredCount={filteredAssistants.length}
                        totalCount={assistants.length}
                        loading={loading}
                    />
                    <div className="wf-filter-main">
                        <DSResourceList
                            variant="list-vivid"
                            title="Workforce"
                            description="Each assistant is a deployed digital employee. Click to open, or start a new one from a blueprint."
                            icon={Bot}
                            iconTone="violet"
                            headerRight={<ModeSegmented mode={mode} onChange={setMode} />}
                            items={filteredAssistants}
                            loading={loading}
                            error={error}
                            getKey={(a) => a.assistant_id}
                            getCard={(a) => {
                                const live = a.status === 'active' && !a.disabled;
                                const opcodes = Array.isArray(a.opcodes) ? a.opcodes.length : 0;
                                const channels = a.channels?.authorized_channels?.length || 0;
                                return {
                                    tone: live ? 'green' : 'orange',
                                    title: a.title || a.assistant_id,
                                    description: a.description || '',
                                    version: `v${a.version || '0.1.0'}`,
                                    stats: [
                                        {
                                            icon: Layers,
                                            value: opcodes,
                                            label: opcodes === 1 ? 'opcode' : 'opcodes',
                                            chipTone: 'blue',
                                        },
                                        {
                                            icon: Radio,
                                            value: channels,
                                            label: channels === 1 ? 'channel' : 'channels',
                                            chipTone: 'violet',
                                        },
                                    ],
                                    createdAt: a.created || null,
                                    searchTerms: [a.title, a.description, a.assistant_id],
                                };
                            }}
                            onPick={openWorkspace}
                            onEdit={openWorkspace}
                            onDelete={(a) => setConfirmDelete(a)}
                            extraAction={{ label: 'Provision', icon: Rocket, onClick: (a) => setProvisionFor(a) }}
                            onCreate={() => setShowBp(true)}
                            newLabel="New Assistant"
                            searchPlaceholder="Search assistants by name, description, or ID…"
                            emptyTitle={assistants.length === 0
                                ? 'No assistants yet'
                                : filtersActive
                                    ? 'No assistants match filters'
                                    : 'No assistants yet'}
                            emptyDescription={assistants.length === 0
                                ? "Spin up your first one from a blueprint — it'll be live in seconds."
                                : filtersActive
                                    ? 'Try clearing or adjusting filters on the left.'
                                    : "Spin up your first one from a blueprint — it'll be live in seconds."}
                            emptyIcon={Bot}
                        />
                    </div>
                </div>
            </div>

            <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this assistant?</AlertDialogTitle>
                        <AlertDialogDescription>
                            <b>{confirmDelete?.title || confirmDelete?.assistant_id}</b> will be permanently removed.
                            Any active channel bindings will stop working immediately.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => { e.preventDefault(); handleDelete(confirmDelete); }}
                            disabled={isDeleting}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            {isDeleting ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Deleting…
                                </>
                            ) : 'Delete'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <BlueprintModal
                open={showBp}
                onClose={() => setShowBp(false)}
                userOrg={oragID}
                userSub={subOragID}
                onDeployed={handleDeployed}
            />

            <ProvisionDialog
                open={!!provisionFor}
                assistant={provisionFor || { assistant_id: '', title: '' }}
                organizationId={oragID}
                subOrganizationId={subOragID}
                onClose={() => setProvisionFor(null)}
                onCompleted={() => setRefreshTick((t) => t + 1)}
            />
        </div>
    );
}

function ModeSegmented({ mode, onChange }) {
    return (
        <div className="ds-product-tabs" role="tablist" aria-label="Builder mode">
            <button
                type="button"
                role="tab"
                aria-selected={mode === 'gui'}
                onClick={() => onChange('gui')}
                className={cn('ds-product-tab', mode === 'gui' && 'ds-product-tab-active')}
            >
                GUI Builder
            </button>
            <button
                type="button"
                role="tab"
                disabled
                aria-selected={false}
                title="Coming soon — describe what you want and AI builds it."
                className="ds-product-tab"
                style={{ cursor: 'not-allowed', opacity: 0.5 }}
            >
                Build with AI <span style={{ marginLeft: 4, fontSize: 10, fontWeight: 500, opacity: 0.7 }}>· soon</span>
            </button>
        </div>
    );
}
